import {
  startOfDay,
  endOfDay,
  startOfMonth,
  endOfMonth,
  subDays,
  subMonths,
  format,
} from "date-fns";

// Indian Financial Year: April 1 -> March 31
// fyStart for date -> the year in which the FY started
export const fyStartYear = (d) => {
  const dt = d instanceof Date ? d : new Date(d);
  return dt.getMonth() >= 3 ? dt.getFullYear() : dt.getFullYear() - 1;
};

export const fyLabel = (startYear) =>
  `FY${String(startYear).slice(-2)}-${String(startYear + 1).slice(-2)}`;

export const fyRange = (startYear) => [
  startOfDay(new Date(startYear, 3, 1)), // 1 Apr
  endOfDay(new Date(startYear + 1, 2, 31)), // 31 Mar
];

// q in 1..4. FY Q1 = Apr-Jun, Q2 = Jul-Sep, Q3 = Oct-Dec, Q4 = Jan-Mar
export const fyQuarterRange = (startYear, q) => {
  const startMonthIndex = 3 + (q - 1) * 3; // 3,6,9,12
  const sYear = startYear + Math.floor(startMonthIndex / 12);
  const sMonth = startMonthIndex % 12;
  const start = startOfDay(new Date(sYear, sMonth, 1));
  const endMonthIndex = startMonthIndex + 2;
  const eYear = startYear + Math.floor(endMonthIndex / 12);
  const eMonth = endMonthIndex % 12;
  const end = endOfMonth(new Date(eYear, eMonth, 1));
  return [start, endOfDay(end)];
};

export const monthRange = (year, monthIndex) => [
  startOfDay(new Date(year, monthIndex, 1)),
  endOfDay(endOfMonth(new Date(year, monthIndex, 1))),
];

// FY-quarter index from a calendar month. Apr-Jun=Q1, Jul-Sep=Q2, Oct-Dec=Q3, Jan-Mar=Q4.
const fyQuarterFromMonth = (m) => {
  if (m >= 3 && m <= 5) return 1;
  if (m >= 6 && m <= 8) return 2;
  if (m >= 9 && m <= 11) return 3;
  return 4;
};

const today = () => startOfDay(new Date());

export const buildPresets = () => {
  const t = today();
  const fyNow = fyStartYear(t);
  return [
    { id: "all", label: "All time", range: () => [null, null] },
    { id: "today", label: "Today", range: () => [t, endOfDay(t)] },
    {
      id: "yesterday",
      label: "Yesterday",
      range: () => {
        const y = subDays(t, 1);
        return [y, endOfDay(y)];
      },
    },
    {
      id: "thisMonth",
      label: "This month",
      range: () => [startOfMonth(t), endOfDay(endOfMonth(t))],
    },
    {
      id: "lastMonth",
      label: "Last month",
      range: () => {
        const lm = subMonths(t, 1);
        return [startOfMonth(lm), endOfDay(endOfMonth(lm))];
      },
    },
    {
      id: "thisQuarter",
      label: "This quarter",
      range: () => fyQuarterRange(fyNow, fyQuarterFromMonth(t.getMonth())),
    },
    {
      id: "lastQuarter",
      label: "Last quarter",
      range: () => {
        const q = fyQuarterFromMonth(t.getMonth());
        const prevFy = q === 1 ? fyNow - 1 : fyNow;
        const prevQ = q === 1 ? 4 : q - 1;
        return fyQuarterRange(prevFy, prevQ);
      },
    },
    { id: "thisFY", label: `This FY (${fyLabel(fyNow)})`, range: () => fyRange(fyNow) },
    { id: "lastFY", label: `Last FY (${fyLabel(fyNow - 1)})`, range: () => fyRange(fyNow - 1) },
  ];
};

const sameDay = (a, b) =>
  a && b && format(a, "yyyy-MM-dd") === format(b, "yyyy-MM-dd");

export const detectPreset = (start, end) => {
  if (!start && !end) return "all";
  const s = start ? startOfDay(new Date(start)) : null;
  const e = end ? startOfDay(new Date(end)) : null;
  for (const p of buildPresets()) {
    const [ps, pe] = p.range();
    const psd = ps ? startOfDay(ps) : null;
    const ped = pe ? startOfDay(pe) : null;
    if (
      (s == null && psd == null) ||
      (s && psd && sameDay(s, psd))
    ) {
      if (
        (e == null && ped == null) ||
        (e && ped && sameDay(e, ped))
      ) {
        return p.id;
      }
    }
  }
  return "custom";
};

export const formatRangeLabel = (start, end) => {
  const presetId = detectPreset(start, end);
  if (presetId !== "custom") {
    const p = buildPresets().find((x) => x.id === presetId);
    if (p) return p.label;
  }
  if (start && end) {
    const s = new Date(start);
    const e = new Date(end);
    if (s.getFullYear() === e.getFullYear()) {
      return `${format(s, "d MMM")} – ${format(e, "d MMM yyyy")}`;
    }
    return `${format(s, "d MMM yyyy")} – ${format(e, "d MMM yyyy")}`;
  }
  if (start) return `From ${format(new Date(start), "d MMM yyyy")}`;
  if (end) return `Until ${format(new Date(end), "d MMM yyyy")}`;
  return "All time";
};
